import { BLOCKED_REASONS, BlockedReason, QuotaResponse } from 'types/billing';
import { TrialInfo, trialDayBand } from 'utils/billing/trialDayBand';

/** Severity tone for the sidebar billing widget, mirroring the hero/pill colors. */
export type SidebarWidgetTone = 'trial' | 'overage' | 'blocked';

/** Value-const for the {@link SidebarWidgetTone} union. */
export const SIDEBAR_WIDGET_TONES = {
  trial: 'trial',
  overage: 'overage',
  blocked: 'blocked',
} as const satisfies Record<string, SidebarWidgetTone>;

export interface SidebarWidgetVM {
  tone: SidebarWidgetTone;
  /** i18n key suffixes under `sidebar.footer.upgrade-now.`. */
  titleKey: string;
  line1Key: string;
  line2Key: string;
  ctaKey: string;
  /** Values are numbers when they drive i18next pluralization (`count`). */
  interpolation?: Record<string, string | number>;
}

/** A `blocked`-tone widget keyed on a {@link BlockedReason} (shared by the BE-set
 *  block branch and the FE trial-lapsed fallback). */
const blockedWidget = (reason: BlockedReason): SidebarWidgetVM => ({
  tone: SIDEBAR_WIDGET_TONES.blocked,
  titleKey: `blocked.${reason}.title`,
  line1Key: `blocked.${reason}.line1`,
  line2Key: `blocked.${reason}.line2`,
  ctaKey: `blocked.${reason}.cta`,
});

/**
 * Maps the billing snapshot to the sidebar widget view-model (PLT-4074) — one of
 * the 3 self-serve state surfaces. Block states take precedence over soft-overage
 * over the trial lifecycle. Returns undefined for healthy/paid (no widget). Pure.
 */
export const deriveSidebarWidget = (
  quota: QuotaResponse | undefined,
  trial: TrialInfo | undefined,
  approaching = false,
  isTrialLapsed = false,
): SidebarWidgetVM | undefined => {
  if (quota?.blocked) {
    return blockedWidget(quota.blocked.reason);
  }

  if (quota?.overage?.active) {
    return {
      tone: SIDEBAR_WIDGET_TONES.overage,
      titleKey: 'overage.title',
      line1Key: 'overage.line1',
      line2Key: 'overage.line2',
      ctaKey: 'overage.cta',
      interpolation: { credits: quota.overage.credits.toLocaleString() },
    };
  }

  if (trial?.active) {
    const ending = trialDayBand(trial) === 'ending';

    // Trial-ending (≤ ending threshold) escalates to the red danger surface AND
    // switches to urgent ending copy ("Free trial ends soon" / "Select a plan"),
    // matching the hero + pill ending band. The early/exploring bands keep the
    // calm purple "Free trial / Review as much as you want" widget.
    if (ending) {
      return {
        tone: SIDEBAR_WIDGET_TONES.blocked,
        titleKey: 'trial-ending.title',
        line1Key: 'trial-ending.line1',
        line2Key: 'trial-ending.line2',
        ctaKey: 'trial-ending.cta',
        // `count` (number) drives "1 day left" vs "N days left" pluralization.
        interpolation: { count: trial.daysRemaining },
      };
    }

    return {
      tone: SIDEBAR_WIDGET_TONES.trial,
      titleKey: 'trial.title',
      line1Key: trial.dayNumber <= 1 ? 'trial.line1.first-day' : 'trial.line1',
      line2Key: 'trial.line2',
      ctaKey: 'trial.cta',
      // `days` feeds the first-day "Unlimited for the next N days" line;
      // `count` (number) drives "1 day left" vs "N days left" pluralization.
      interpolation: {
        days: String(trial.daysRemaining),
        count: trial.daysRemaining,
      },
    };
  }

  // Trial lapsed flag is computed by useAccountBilling (which gates on quota
  // being loaded to avoid flashing the widget at paid accounts whose trial
  // object is stale) and passed in by the caller (PLT-4196).
  if (isTrialLapsed) {
    return blockedWidget(BLOCKED_REASONS.trialEnd);
  }

  // Paid pool nearing its limit — same red "act now" surface as a hard block /
  // trial ending (the `blocked` tone is the shared red treatment).
  if (approaching && quota?.plan) {
    return {
      tone: SIDEBAR_WIDGET_TONES.blocked,
      titleKey: 'approaching.title',
      line1Key: 'approaching.line1',
      line2Key: 'approaching.line2',
      ctaKey: 'approaching.cta',
      interpolation: {
        credits: quota.plan.credits_remaining.toLocaleString(),
      },
    };
  }

  return undefined;
};
